import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  StyledIncrementingStatBlock,
  DescriptionParagraph,
  BlockHeading,
} from './styles.js';

export const IncrementingStatBlock = ({
  finalValue,
  prefix,
  suffix,
  description,
  backgroundColor,
  textColor,
  initialValue = 0,
  increment = 1,
  speed = 50,
}) => {
  const blockRef = useRef(null);

  const [isInView, setIsInView] = useState(false);
  const [currentValue, setCurrentValue] = useState(initialValue);

  useEffect(() => {
    if (!blockRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(blockRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView) {
      return;
    }

    setCurrentValue(initialValue);

    if (initialValue >= finalValue) {
      setCurrentValue(finalValue);
      return;
    }

    let value = initialValue;
    let timeoutId;

    const step = () => {
      value = value += increment;

      if (value >= finalValue) {
        setCurrentValue(finalValue);
        return;
      }

      setCurrentValue(value);
      timeoutId = setTimeout(step, speed);
    };

    timeoutId = setTimeout(step, speed);

    return () => clearTimeout(timeoutId);
  }, [finalValue, initialValue, increment, isInView, speed]);

  return (
    <StyledIncrementingStatBlock
      ref={blockRef}
      backgroundColor={backgroundColor}
    >
      <BlockHeading backgroundColor={backgroundColor} textColor={textColor}>
        {`${prefix ? `${prefix} ` : ''}${parseFloat(currentValue.toFixed(1))}${
          suffix ?? ''
        }`}
      </BlockHeading>
      <DescriptionParagraph textColor={textColor}>
        {description}
      </DescriptionParagraph>
    </StyledIncrementingStatBlock>
  );
};

IncrementingStatBlock.displayName = 'IncrementingStatBlock';

IncrementingStatBlock.propTypes = {
  finalValue: PropTypes.number.isRequired,
  suffix: PropTypes.string,
  prefix: PropTypes.string,
  description: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  initialValue: PropTypes.number,
  increment: PropTypes.number,
  speed: PropTypes.number,
};
